import {useState} from 'react';
import {Link} from 'react-router';

import {productInsertT, productT} from '@/lib/types/product.types';

import {renderProductTableStats} from '@/lib/config/productTableConfig';
import {createDataTableConfig} from '@/lib/helpers/config';

import {useDataQuery, useProductData, useViewQuery} from '@/hooks';

import {useAuth} from '@/providers/components/authProvider';

import {
  ConfirmDeleteModalComponent,
  ModalFormComponent,
  SearchAndSelectComponent,
  StatsRowComponent,
} from '@/components';
import Page from '@/components/Page';
import {DataTable} from '@/components/dataTable';

export const initialProductFormData: productInsertT = {
  created_by: '',
  date_created: '',
  date_modified: '',
};

function AdminProducts() {
  const {isAdmin} = useAuth();

  // State
  const [selectingBrand, setSelectingBrand] = useState(false);
  const [selectedBrandId, setSelectedBrandId] = useState<number | null>(null);
  const [brandSearchTerm, setBrandSearchTerm] = useState<string>('');

  const {
    setFormData,
    isVisible,
    isEditing,
    setIsVisible,
    isDeleteModalVisible,
    handleCancelDeleteProduct,
    isSubmitting,
    submissionError,
    createFields,
    handleAddProductClick,
    handleEditProductClick,
    handleDeleteProductClick,
    handleSubmitProduct,
    handleUpdateProduct,
    handleDeleteProduct,
    handleCloseForm,
    product,
  } = useProductData();

  const {data: stats} = useViewQuery({
    view: 'product_stats',
  });

  const {data: brands} = useDataQuery({
    table: 'Brand',
    columns: ['brand_name', 'brand_id'],
    limit: 10,
    page: 1,
    searchTerm: brandSearchTerm,
    enabled: !!brandSearchTerm,
  });

  // Data Table
  const dataTableConfig = createDataTableConfig({
    table: 'Product',
    title: 'Products Summary',
    searchColumn: 'product_name',
    columns: [
      'product_id',
      'product_name',
      'product_is_active',
      'product_category',
      'product_subcategory',
      'product_strain',
      ['Brand', ['brand_id', 'brand_name']],
    ],
    onAdd: () => setSelectingBrand(true),
    columnsDisplay: [
      {
        header: 'Status',
        field: 'product_is_active',
        render: product => (
          <span className={`badge bg-${!product.date_removed ? 'success' : 'danger'} text-white`}>
            {!product.date_removed ? 'Active' : 'Inactive'}
          </span>
        ),
      },
      {
        header: 'Name',
        field: 'product_name',
        render: product => (
          <Link to={`/products/${product.product_id}`} className="text-primary hover:text-primary dark:text-primary">
            {product.product_name}
          </Link>
        ),
      },
      {
        header: 'Brand',
        field: 'Brand.brand_name',
        render: row => (
          <Link to={`/brands/${row.Brand.brand_id}`} className="text-primary hover:text-primary dark:text-primary">
            {row.Brand.brand_name}
          </Link>
        ),
      },
      {header: 'Category', field: 'product_category'},
      {header: 'Sub Category', field: 'product_subcategory'},
      {header: 'Strain Type', field: 'product_strain'},
    ],
    actionsDisplay: [
      {
        icon: 'ri-pencil-line',
        color: 'info',
        handler: row => handleEditProductClick(row.product_id),
        permission: () => isAdmin,
      },
      {
        icon: 'ri-delete-bin-line',
        color: 'danger',
        handler: row => handleDeleteProductClick(row.product_id),
        permission: () => isAdmin,
      },
    ],
  });

  // Stats
  const statsRow = renderProductTableStats(stats?.total, stats?.active, stats?.inactive);

  return (
    <Page currentPage="Products">
      <StatsRowComponent stats={statsRow} />
      <DataTable {...dataTableConfig} />

      <ModalFormComponent
        isVisible={isVisible && ((!selectingBrand && selectedBrandId !== null) || isEditing)}
        fields={createFields}
        formData={product || initialProductFormData}
        onSubmit={(data: productT) => {
          if (isEditing) {
            handleUpdateProduct(data);
          } else {
            handleSubmitProduct({...data, fk_brand_id: selectedBrandId!});
          }
        }}
        setFormData={setFormData}
        error={submissionError}
        isSubmitting={isSubmitting}
        title={isEditing ? 'Edit Product' : 'Create New Product'}
        onClose={handleCloseForm}
      />

      <ConfirmDeleteModalComponent
        title="Confirm Deletion"
        description="Are you sure you want to delete this product? This action cannot be undone."
        isVisible={isDeleteModalVisible}
        isSubmitting={isSubmitting}
        onClose={handleCancelDeleteProduct}
        onConfirm={handleDeleteProduct}
      />
      <SearchAndSelectComponent
        title="Search and Select Brand"
        isVisible={selectingBrand}
        onClose={() => {
          setSelectingBrand(false);
          setSelectedBrandId(null);
        }}
        onSelect={item => {
          setSelectingBrand(false);
          setSelectedBrandId(item.brand_id);
          setIsVisible(true);
          handleAddProductClick();
        }}
        onSearch={idx => {
          // getBrandResults(idx);
          setBrandSearchTerm(idx);
        }}
        overrideClose={true}
        results={brands?.data || []}
        mapStructure={{
          displayFields: [{title: 'Brand Name', field: 'brand_name'}],
          valueField: 'brand_id',
        }}
      />
    </Page>
  );
}

export default AdminProducts;
